import { z } from 'zod';
import { tipoDeCierreSchema } from '../eventos.js';
import { faseSchema } from '../programa/fases.js';
import { cohorteSchema, distribucionDeSemaforoSchema } from './clinico.js';
/**
 * Contrato de la administración de Fundación · A-01 (el panel de la red) y A-02
 * (las instituciones), ADMIN-1.
 *
 * **Acá no viaja ninguna persona.** Fundación no tiene identidad de pacientes
 * (ADR-020) y no ve pacientes de ninguna institución (`roles.ts`): lo que
 * devuelven estas rutas son cuentas y estados. Ningún alias de paciente, ningún
 * número de caso, ningún id de programa, ninguna fecha de una persona. El único
 * texto que no es de la institución es el alias de equipo de quien la administra,
 * que es una cuenta del equipo y no un dato de paciente.
 *
 * **Y no compara instituciones.** Cada fila se lee sola: no hay ranking, no hay
 * porcentajes de adherencia por institución y el orden es alfabético. La
 * reportería de red (Nivel 3) está diferida (`CLAUDE.md` §4.2).
 *
 * Los objetos son estrictos a propósito: un campo nuevo que alguien agregue en
 * la api sin pasar por acá hace fallar la validación, en vez de salir solo.
 */
/**
 * El estado de una institución, **derivado** y nunca cargado a mano:
 *
 *   - `ACTIVA`: tiene al menos un programa activo.
 *   - `PUESTA_EN_MARCHA`: creada y sin ningún paciente todavía.
 *   - `SIN_PROGRAMAS_ACTIVOS`: tuvo pacientes y hoy no tiene ninguno en
 *     programa (todos cerraron). No es una puesta en marcha, y decir "Activa"
 *     sin pacientes sería inventarlo.
 *   - `PAUSADA`: el tenant está desactivado (`activo = false`): nadie de la
 *     institución entra y su historial se conserva.
 */
export const estadoDeInstitucionSchema = z.enum([
    'ACTIVA',
    'PUESTA_EN_MARCHA',
    'SIN_PROGRAMAS_ACTIVOS',
    'PAUSADA',
]);
const cuenta = z.number().int().nonnegative();
/** Una institución en la lista: sus cuentas, nunca sus pacientes. */
export const filaDeInstitucionSchema = z.strictObject({
    id: z.uuid(),
    nombre: z.string().min(1).max(120),
    slug: z.string().min(1).max(80),
    /** Nula mientras `provisionar` no la cargue: no se inventa. */
    ciudad: z.string().max(80).nullable(),
    /** Desde cuándo está en la red: el alta de la institución en la base. */
    desde: z.iso.datetime(),
    estado: estadoDeInstitucionSchema,
    pacientesActivos: cuenta,
    /** Personas del equipo con cuenta activa: profesionales, coordinación y administración. */
    equipo: cuenta,
    /** Programas con el protocolo de silencio agotado (definición del 28/10). */
    sinContacto: cuenta,
    respondieronHoy: cuenta,
    /** El alias de equipo de quien la administra. Nunca su correo. */
    administradaPor: z.string().max(80).nullable(),
});
/** A-01 · el panel de la red. */
export const panelDeLaRedSchema = z.strictObject({
    instituciones: z.strictObject({
        total: cuenta,
        activas: cuenta,
        enPuestaEnMarcha: cuenta,
        sinProgramasActivos: cuenta,
        pausadas: cuenta,
    }),
    /** Programas activos de las instituciones no pausadas. */
    pacientesEnPrograma: cuenta,
    equiposDeSalud: cuenta,
    sinContacto: cuenta,
    /** Orden alfabético por nombre: no es un ranking. */
    porInstitucion: z.array(filaDeInstitucionSchema),
    semaforo: distribucionDeSemaforoSchema,
    porFase: z.partialRecord(faseSchema, cuenta),
    actividad: z.strictObject({
        respondieronHoy: cuenta,
        contactosDeHoy: cuenta,
        evaluacionesProximas7d: cuenta,
        cerradosEsteMes: cuenta,
        /** Los mismos cierres, por tipo (CIERRES-1). Sin nombres: sólo cuántos. */
        cerradosPorTipo: z.partialRecord(tipoDeCierreSchema, cuenta),
        altasEsteMes: cuenta,
    }),
    /** Sólo lo que hoy se puede afirmar sin inventar un estado. */
    sistema: z.strictObject({
        /** Si esta respuesta llegó, la api responde: no hay otro valor posible. */
        api: z.literal('responde'),
        fhir: z.strictObject({
            /** Si la api tiene configurado el FHIR store. */
            configurado: z.boolean(),
            /** Escrituras en el buffer que todavía no salieron. */
            pendientes: cuenta,
            /** Escrituras que fallaron y esperan el reintento. */
            fallidas: cuenta,
        }),
        correo: z.enum(['sendgrid', 'local']),
        /**
         * El corpus de PIA de la red (PIA-1): cuántas piezas, cuántas esperan que
         * Fundación las apruebe y cuántas usa PIA.
         */
        contenidosDePia: z.strictObject({
            piezas: cuenta,
            porAprobar: cuenta,
            publicadas: cuenta,
        }),
    }),
});
/** A-02 · la lista de instituciones. */
export const listaDeInstitucionesSchema = z.strictObject({
    instituciones: z.array(filaDeInstitucionSchema),
});
/**
 * A-02 · la ficha de una institución: su fila, su Operación resumida **sin la
 * lista de casos** y la puesta en marcha como hechos.
 */
export const fichaDeInstitucionSchema = z.strictObject({
    institucion: filaDeInstitucionSchema,
    zonaHoraria: z.string().min(1).max(64),
    /** La versión vigente de las reglas del motor. Nula si nunca se sembró. */
    reglas: z
        .strictObject({ version: z.number().int().positive(), desde: z.iso.datetime() })
        .nullable(),
    operacion: z.strictObject({
        cohorte: cohorteSchema,
        silencioAgotado: cuenta,
        contactosPerdidos7d: cuenta,
        evaluacionesProximas7d: cuenta,
        notasPublicadas7d: cuenta,
    }),
    puestaEnMarcha: z.strictObject({
        creadaAt: z.iso.datetime(),
        administracionConCuenta: z.boolean(),
        /** Profesionales y coordinación con cuenta activa. */
        equipoCargado: cuenta,
        /** Si ya entró algún paciente, en algún momento. Sin la fecha: es de una persona. */
        primerPaciente: z.boolean(),
    }),
});
/** El `:id` de la ficha: un uuid, validado antes de que llegue a la base. */
export const parametrosDeInstitucionSchema = z.strictObject({ id: z.uuid() });
//# sourceMappingURL=fundacion.js.map